import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Get API base URL from environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8083';

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000,
    withCredentials: true,
});


// Response interceptor - handle errors globally and token refresh
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        if (error.response) {
            // Server responded with error status
            switch (error.response.status) {
                case 401: {
                    const currentPath = window.location.pathname;
                    const isLoginPage = currentPath === '/login';
                    const isAuthCallback = currentPath.startsWith('/auth/');
                    const requestUrl = error.config?.url || '';

                    // /me/refresh returning 401 means the refresh token is expired —
                    // redirect to login regardless of current page
                    const isRefreshEndpoint = requestUrl.includes('/me/refresh');

                    // Other /me/* endpoints (validate, revoke, etc.) are handled by
                    // AuthContext — don't redirect so it can fail gracefully
                    const isOtherAuthEndpoint =
                        !isRefreshEndpoint && requestUrl.includes('/me/');

                    // Check if user is actively logging out - don't redirect in this case
                    const isLoggingOut = sessionStorage.getItem('isLoggingOut') === 'true';

                    if (!isLoginPage && !isAuthCallback && !isOtherAuthEndpoint && !isLoggingOut) {
                        // Preserve CMS path so we can redirect back after login if needed
                        const isCMS = currentPath.startsWith('/cms');
                        window.location.href = isCMS
                            ? `/login?redirect=${encodeURIComponent(currentPath)}`
                            : '/login';
                    }
                    break;
                }
                case 403:
                    console.error('Forbidden - You do not have permission');
                    break;
                case 404:
                    console.error('Resource not found');
                    break;
                case 500:
                    console.error('Internal server error');
                    break;
                default:
                    console.error('API Error:', error.response.data);
            }
        } else if (error.request) {
            // Request was made but no response received
            console.error('Network error - No response from server');
        } else {
            // Something else happened
            console.error('Error:', error.message);
        }
        return Promise.reject(error);
    }
);

// API Service object with HTTP methods
const api = {
    /**
     * GET request
     * @param url - API endpoint
     * @param config - Optional axios config
     */
    get: async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
        try {
            const response: AxiosResponse<T> = await apiClient.get(url, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * POST request
     * @param url - API endpoint
     * @param data - Request body data
     * @param config - Optional axios config
     */
    post: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        try {
            const response: AxiosResponse<T> = await apiClient.post(url, data, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * PUT request
     * @param url - API endpoint
     * @param data - Request body data
     * @param config - Optional axios config
     */
    put: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        try {
            const response: AxiosResponse<T> = await apiClient.put(url, data, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * PATCH request
     * @param url - API endpoint
     * @param data - Request body data
     * @param config - Optional axios config
     */
    patch: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        try {
            const response: AxiosResponse<T> = await apiClient.patch(url, data, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * DELETE request
     * @param url - API endpoint
     * @param config - Optional axios config
     */
    delete: async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
        try {
            const response: AxiosResponse<T> = await apiClient.delete(url, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Upload file with FormData
     * @param url - API endpoint
     * @param formData - FormData object with file
     * @param onUploadProgress - Progress callback
     */
    upload: async <T = any>(
        url: string,
        formData: FormData,
        onUploadProgress?: (progressEvent: any) => void
    ): Promise<T> => {
        try {
            const response: AxiosResponse<T> = await apiClient.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress,
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },
};

export default api;

// Export the axios instance for advanced usage
export { apiClient };